import { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { useSelector, useDispatch } from 'react-redux';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

import Button from 'core/buttons/Button';
import MoreActions from 'core/lab/MoreActions';
import ReloadButton from 'core/buttons/ReloadButton';
import TableGridFilter from 'core/table/TableGridFilter';
import ColumnVisibility from 'core/table/ColumnVisibility';
import TableActionsContainer from 'core/table/TableActionsContainer';

import Sheet from 'utils/Sheet';
import app from 'app/config/app';
import FilterType from 'app/config/FilterType';
import useQueryParams from 'hooks/useQueryParams';
import { getFlashPaymentWebhookLogs } from 'app/features/flash-payment-webhook-logs/flashPaymentWebhookLogSaga';
import {
  setParam,
  deleteFilterParams,
} from 'app/features/flash-payment-webhook-logs/flashPaymentWebhookLogSlice';

export default function FlashPaymentWebhookLogsActions({
  table,
  filterByCustomer,
  defaultFilterQueries,
}) {
  const dispatch = useDispatch();

  const [showFilter, setShowFilter] = useState(false);

  const logsState = useSelector((state) => state.flashPaymentWebhookLogs.index);

  const [queryParams, updateQueryParams] = useQueryParams(app.ignoreFilterKeys);

  const availableFilters = [
    ...(!filterByCustomer
      ? [
          {
            label: 'Customer ID',
            value: 'CustomerId',
            type: FilterType.input,
          },
        ]
      : []),
    {
      label: 'Flash Payment ID',
      value: 'FlashPaymentId',
      type: FilterType.input,
    },
    {
      label: 'Flash Payment User ID',
      value: 'FlashPaymentUserId',
      type: FilterType.input,
    },
    {
      label: 'Show Master Account Txn Log',
      value: 'ShowMasterAccountTxnLog',
      type: FilterType.select,
      options: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
      ],
    },
    {
      label: 'From Date',
      value: 'FromDate',
      type: FilterType.datepicker,
      props: { withStartDayTimezone: true },
    },
    {
      label: 'To Date',
      value: 'ToDate',
      type: FilterType.datepicker,
      props: { withEndDayTimezone: true },
    },
  ];

  const { params: appliedFilters } = logsState;

  const handleOnFilter = useCallback(
    (data) => {
      dispatch(setParam({ ...data, ...defaultFilterQueries, page_number: 1 }));
    },
    [dispatch, defaultFilterQueries],
  );

  const handleOnReset = useCallback(() => {
    dispatch(deleteFilterParams(availableFilters.map((filter) => filter.value)));
  }, [dispatch, filterByCustomer]);

  useEffect(() => {
    updateQueryParams(appliedFilters);
  }, [JSON.stringify(appliedFilters)]);

  useEffect(() => {
    dispatch(setParam(queryParams));
  }, [JSON.stringify(queryParams)]);

  return (
    <>
      <Box>
        <TableGridFilter
          showFilter={showFilter}
          appliedFilters={appliedFilters}
          availableFilters={availableFilters}
          onFilter={handleOnFilter}
          onReset={handleOnReset}
        />
      </Box>
      <TableActionsContainer>
        <Box />
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            endIcon={<FilterAltIcon />}
            onClick={() => setShowFilter(!showFilter)}
            uppercase
          >
            Filter
          </Button>
          {table && <ColumnVisibility table={table} />}
          <ReloadButton
            onClick={() => {
              dispatch(
                getFlashPaymentWebhookLogs({
                  ...appliedFilters,
                  page_number: 1,
                  ...defaultFilterQueries,
                }),
              );
            }}
          />
          <MoreActions
            onExportToCsv={() =>
              Sheet.exportReactTableSelectedRowsToCsv(table, 'flash_payment_webhook_logs_')
            }
            onExportToExcel={() =>
              Sheet.exportReactTableSelectedRowsToExcel(table, 'flash_payment_webhook_logs_')
            }
          />
        </Box>
      </TableActionsContainer>
    </>
  );
}

FlashPaymentWebhookLogsActions.propTypes = {
  table: PropTypes.object,
  filterByCustomer: PropTypes.bool,
  defaultFilterQueries: PropTypes.object,
};

FlashPaymentWebhookLogsActions.defaultProps = {
  filterByCustomer: false,
  defaultFilterQueries: {},
};
